import type { CommandProperties } from '@atlassian/rovo-content-bridge-api/command';
import { createCommand } from '@atlassian/rovo-content-bridge-api/command-creator';

import type { NativeEmbedChildCommand, NativeEmbedCommand } from '../../../common/types';
import type { NativeEmbedFeature } from '../../../common/types/features/main';

export interface NativeEmbedCommandParams extends CommandProperties, NativeEmbedCommand {}

export interface NativeEmbedChildCommandParams extends CommandProperties, NativeEmbedChildCommand {}

export const NativeEmbedBridgeCommand =
	createCommand<NativeEmbedCommandParams>('native-embed-command');

export const NativeEmbedChildBridgeCommand = createCommand<NativeEmbedChildCommandParams>(
	'native-embed-child-command',
);

/**
 * Command params for the feature handshake command.
 */
export interface NativeEmbedFeatureHandshakeCommandParams extends CommandProperties {
	features: NativeEmbedFeature[];
}

export const NativeEmbedFeatureHandshakeCommand =
	createCommand<NativeEmbedFeatureHandshakeCommandParams>('native-embed-feature-handshake-command');

/**
 * Command params for selected-change events (parent → child).
 * Emitted when the embed's selection state changes.
 */
export interface NativeEmbedSelectedChangeCommandParams extends CommandProperties {
	selected: boolean;
}

export const NativeEmbedSelectedChangeCommand =
	createCommand<NativeEmbedSelectedChangeCommandParams>('native-embed-selected-change-command');

/**
 * View mode for the embed container (parent → child).
 */
export type NativeEmbedViewMode = 'regular' | 'maximize';

/**
 * Command params for view-mode-change events (parent → child).
 * Emitted when the embed's view mode changes (e.g. full screen / maximized vs regular).
 */
export interface NativeEmbedViewModeChangeCommandParams extends CommandProperties {
	viewMode: NativeEmbedViewMode;
}

export const NativeEmbedViewModeChangeCommand =
	createCommand<NativeEmbedViewModeChangeCommandParams>('native-embed-view-mode-change-command');

/**
 * Command params for click events forwarded from child to host
 * when notify-host-click is enabled.
 */
export interface NativeEmbedClickEventCommandParams extends CommandProperties {}

export const NativeEmbedClickEventCommand = createCommand<NativeEmbedClickEventCommandParams>(
	'native-embed-click-event-command',
);

/**
 * Command params for always-show-title-change events (parent → child).
 * Emitted when the user toggles the always-show-title option.
 */
export interface NativeEmbedAlwaysShowTitleChangeCommandParams extends CommandProperties {
	alwaysShowTitle: boolean;
}

export const NativeEmbedAlwaysShowTitleChangeCommand =
	createCommand<NativeEmbedAlwaysShowTitleChangeCommandParams>(
		'native-embed-always-show-title-change-command',
	);

/**
 * Command params for toggle-full-screen requests forwarded from child to host
 * when toggle-full-screen is enabled.
 */
export interface NativeEmbedToggleFullScreenCommandParams extends CommandProperties {}

export const NativeEmbedToggleFullScreenCommand =
	createCommand<NativeEmbedToggleFullScreenCommandParams>(
		'native-embed-toggle-full-screen-command',
	);

/**
 * Command params for resize requests forwarded from child to host.
 * The child sends its desired pixel dimensions; the host persists them
 * to the ADF node parameters.
 */
export interface NativeEmbedRequestResizeCommandParams extends CommandProperties {
	height: number;
	width: number;
}

export const NativeEmbedRequestResizeCommand = createCommand<NativeEmbedRequestResizeCommandParams>(
	'native-embed-request-resize-command',
);
